import React, { useEffect, useState } from 'react';
import { SearchInput, B1, P1 } from 'pfm-ui-components';
import styled from 'styled-components';
import { up } from 'styled-breakpoints';
import OutsideClickHandler from 'react-outside-click-handler';
import Container from '../Container';
import { ON_NO_RESULT_FOUND } from '../../../constants/Search';
import Skeleton from 'react-loading-skeleton';
import { pxToRem } from '../../../styles/utils';
import { NO_DATA_FOUND } from '../../../constants/HoldingPage';

interface SearchProps {
  name: string; // name given to the search bar
  value: string; // value for the input tag
  placeholder: string;
  hasClear?: boolean; // to enable clear text in the search bar
  filter?: boolean; // to enable filtering of search option
  onNoResultFound?: string; // Text to display when do search result is found
  headerText?: String; // Header Text for searchBar
  onChange: (arg0: string) => void; // onChange function to handle input value
  onClear?: Function; // function to clear input value
  onEnter?: Function; // function to submit the input value when Enter button is hit
  onSelect: Function; // function returns selected option value
  SearchResult?: Array<string>; // list of searchResults for the given input value
  classname?: string;
}

const SearchContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const HeaderWrapper = styled.div`
  text-align: center;
  width: 100%;
  margin: 15px 1px;
  ${up('md')} {
    margin: 20px 0;
    padding: 13px;
  }
`;

const Header = styled(B1)`
  line-height: 14px;
  font-size: Lato;
  color: #4a4a4a;
  z-index: 0;
  ${up('md')} {
    font-size: 20px;
    line-height: 24px;
  }
`;

const SearchWrapper = styled.div<{ enableBackgroundBlur: boolean }>`
  width: 100%;
  position: relative;
  border-radius: 8px;
  :focus-within {
    box-shadow: 0 0.095em 8px 3px #ed116429;
    z-index: ${(props) => (props.enableBackgroundBlur ? '2' : '0')};
    ${up('md')} {
      box-shadow: 0 0.095em 8px 4px #ed116429;
    }
  }

  ${up('md')} {
    border-radius: 12px;
  }
`;

const SearchBar = styled(SearchInput)`
  border: none;
  width: 100%;
  background: white;
  border-radius: ${(props) => (props.searchBarBorder ? '8px 8px 0 0' : '8px')};
  border-bottom: 1px solid #f0f0f0;
  box-shadow: ${(props) =>
    props.enableBackgroundBlur ? '0 0.095em 8px 1000px #00000029' : '0 0.09375em 8px #00000029'};

  & input {
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(19)};
  }

  :focus {
    box-shadow: 0 0.95em 0.9375em #ed116429;
  }
  & input ::placeholder {
    font-style: normal;
    font-size: 16px;
    line-height: 19px;
  }
  ${up('md')} {
    border: none;
    box-shadow: 0 0.09375em 8px #00000029;
    border-radius: ${(props) => (props.searchBarBorder ? '12px 12px 0 0' : '12px')};
  }

  &.mini-search-bar {
    height: 40px;
    padding-right: 0;
    padding-left: ${pxToRem(2)};
    box-shadow: 0 ${pxToRem(2)} ${pxToRem(8)} #00000029;
    & input {
      max-height: ${pxToRem(40)};
      line-height: ${pxToRem(15)};
      margin-left: ${pxToRem(4)};
    }

    & input ::placeholder {
      font-size: ${pxToRem(12)};
      ${up('md')} {
        font-size: ${pxToRem(16)};
      }
    }
    ${up('md')} {
      height: ${pxToRem(52)};
      padding: ${pxToRem(16)};
    }
  }
`;

const DropDownWrapper = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  background-color: ${(props) => props.theme.palette.white};
  max-height: 327px;
  overflow: auto;
  box-shadow: 0 12px 8px -8px #ed116429, -12px 0 8px -8px #ed116429, 12px 0 8px -8px #ed116429;
  border-radius: 0 0 8px 8px;
  scrollbar-color: ${(props) => props.theme.palette.grey4} ${(props) => props.theme.palette.white};
  scrollbar-width: thin;
  padding-top: 10px;
  ::-webkit-scrollbar {
    width: 18px;
    ${up('md')} {
      width: 34px;
    }
  }
  /* ::-webkit-scrollbar-thumb {
    border-top: 0.88em;
    border: 7.5px solid ${(props) => props.theme.palette.white};
    border-radius: 12px;
    background-clip: padding-box;
    height: 40%;
    background-color: ${(props) => props.theme.palette.grey4};
    ${up('md')} {
      height: 45%;
      border-top: -0.88em;
      border-radius: 18px;
      border: 0.88em solid ${(props) => props.theme.palette.white};
    }
  } 

  ::-webkit-scrollbar-corner {
    background-color: transparent;
  } */

  ${up('md')} {
    border: none;
    scrollbar-width: 18px;
    max-height: 350px;
    border-radius: 0 0 12px 12px;
  }
  &.mini-search-bar {
    max-height: 200px;
  }
`;

const OptionWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const Option = styled(P1)`
  display: block;
  width: auto;
  background: ${(props) => props.theme.palette.white};
  color: #4a4a4a;
  font-size: 14px;
  cursor: pointer;
  line-height: 17px;
  padding: 9px 0 9px 12px;

  ${up('md')} {
    font-size: 16px;
    line-height: 19px;
    padding-left: 16px;
  }

  & .matched-text {
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    line-height: 17px;
    color: ${(props) => props.theme.palette.burgundy1};
    font-weight: 600;
    ${up('md')} {
      font-size: 16px;
      line-height: 19px;
    }
  }
`;

const SearchResultSkeleton = styled(Skeleton)`
  max-width: 90%;
  padding: ${pxToRem(8)} 0;
  margin: 0 2px 12px 8px;
  ${up('md')} {
    margin-left: 24px;
  }
`;
const Search = (props: SearchProps) => {
  const {
    placeholder,
    value,
    name,
    SearchResult,
    hasClear,
    onChange,
    onClear,
    onEnter,
    filter,
    onSelect,
    headerText,
    onNoResultFound,
    classname,
  } = props;
  const [searchList, setSearchList] = useState(SearchResult);
  const [enableBackgroundBlur, setEnableBackgroundBlur] = useState(false);
  useEffect(() => {
    setSearchList(SearchResult);
  }, [SearchResult]);
  /*
   * onHandleChange - this function gets invoked when filter value is set to true
   * @{params} - value - the value entered in searchbar
   */
  const onHandleChange = (value: string) => {
    const userInput = value;
    let filteredSuggestions = SearchResult;
    if (filter && SearchResult) {
      filteredSuggestions = SearchResult.filter((option) => option.toLowerCase().includes(userInput.toLowerCase()));
      if (value && value.length > 0 && filteredSuggestions && filteredSuggestions.length === 0) {
        // filteredSuggestions.push(onNoResultFound);
        setSearchList(filteredSuggestions);
      } else {
        setSearchList(filteredSuggestions);
      }
    }

    onChange(value);
  };

  const onHandleEnter = () => {
    setSearchList([]);
    onEnter();
  };

  /*
   * renderOptionList - this function handler is used to highlight the option matched with the search Input value
   * @{param} - data - object of searchResult
   * @{return} - string
   */
  const renderOptionList = (optionName: string, index: number) => {
    const getHighlightedText = () => {
      var response = optionName;
      try {
        const keywords = value.split(/\s/);
        let regex = new RegExp(`(${keywords.join('|')})`, 'gi');
        if (optionName) {
          var response = optionName.replace(regex, (str: string) => {
            return '<span class=matched-text>' + str + '</span>';
          });
        } else {
          var response = optionName.replace(regex, (str: string) => {
            return '<span>' + str + '</span>';
          });
        }
      } catch {
        // ignore the  unterminated () error
      }
      return response;
    };

    const onOptionSelected = () => {
      onSelect(index, optionName);
      // setSearchList([]);
      onChange('');
    };
    return (
      <OptionWrapper key={index} onClick={() => onOptionSelected()}>
        <Option key={index} dangerouslySetInnerHTML={{ __html: getHighlightedText() }}></Option>
      </OptionWrapper>
    );
  };

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        if (searchList) {
          onChange('');
          // setSearchList([]);
          setEnableBackgroundBlur(false);
        }
      }}
    >
      <SearchContainer>
        {headerText && (
          <HeaderWrapper>
            <Header>{headerText}</Header>
          </HeaderWrapper>
        )}
        <SearchWrapper enableBackgroundBlur={enableBackgroundBlur} onFocus={() => setEnableBackgroundBlur(true)}>
          <SearchBar
            placeholder={placeholder}
            name={name}
            value={value}
            Autocomplete={false}
            hasClear={hasClear}
            onChange={(e: any) => onHandleChange(e.target.value)}
            onClear={onClear}
            onEnter={onEnter}
            searchBarBorder={value && value.length > 0 && ((searchList && searchList.length >= 0) || !searchList)}
            enableBackgroundBlur={enableBackgroundBlur}
            className={classname}
          />
          {/* // this is used to show skeleton when we are waiting for search result */}
          {!searchList && value && value.length > 0 && (
            <DropDownWrapper>
              {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                <SearchResultSkeleton key={`search-bar-skeleton-${index}`} />
              ))}
            </DropDownWrapper>
          )}
          {value && value.length > 0 && searchList && searchList.length > 0 && (
            <DropDownWrapper className={classname}>
              {searchList.map((data: string, index: number) => renderOptionList(data, index))}
            </DropDownWrapper>
          )}
          {value && value.length > 0 && searchList && searchList.length === 0 && (
            <DropDownWrapper
              onClick={() => {
                onChange('');
                setEnableBackgroundBlur(false);
              }}
            >
              <NoResultFoundWrapper>{onNoResultFound}</NoResultFoundWrapper>
            </DropDownWrapper>
          )}
        </SearchWrapper>
      </SearchContainer>
    </OutsideClickHandler>
  );
};

const NoResultFoundWrapper = styled(P1)`
  padding: ${pxToRem(12)};
  z-index: 1;
`;
Search.defaultProps = {
  hasClear: false,
  filter: false,
  onNoResultFound: ON_NO_RESULT_FOUND,
};

export default Search;
